/**
 * SSD Intranet Global Admin Settings Sync Engine
 * Real-time synchronization of admin modifications (sheet renames, team mappings,
 * tab selections, employee assignments) across all admins and users.
 */
import { getGlobalAdminSettingsApi, saveGlobalAdminSettingsApi } from '../api/client';
import { DEFAULT_ROLE_MAPPINGS } from '../config/rolePermissions';
import type { UserRole } from '../types/roles';
import { getAuthToken } from '../contexts/AuthContext';

export interface ActiveEmployeeRecord {
  email: string;
  name: string;
  displayName?: string;
  status: string;
  employeeType: string;
  team?: string;
}

export type GroupVisibilityMode = 'everyone' | 'own_only' | 'public_and_own';

export interface PrintableDocGroup {
  id: string;
  name: string;
  archived?: boolean;
  documentIds: string[];
  createdBy?: string;
  createdByName?: string;
  visibility?: 'public' | 'private' | 'restricted';
  allowedEmails?: string[];
  createdAt?: number;
  updatedAt?: number;
}

export interface GlobalAdminSettings {
  customSheetTitles: Record<string, string>;
  resourcePermissions: Record<string, { team?: string; accessEmails?: string[]; categories?: string[]; customDisplayName?: string }>;
  customTabSelections: Record<string, boolean>;
  userMappings: Record<string, { roles: UserRole[]; primaryTeam: string; assignedTeams: string[] }>;
  teamMembers: Record<string, string[]>;
  customTeams: string[];
  archivedTeams: string[];
  deletedTeams: string[];
  discoveredTabs: Array<{ id: string; ssId: string; tabName: string; name: string; url: string; team: string; access: string; desc: string }>;
  employeeDirectory: Record<string, string>;
  activeEmployees: ActiveEmployeeRecord[];
  terminatedEmails: string[];
  sheetCustomTeams: string[];
  teamSheetOrder?: Record<string, string[]>;
  printableDocGroups?: PrintableDocGroup[];
  printableDocGroupVisibilityMode?: GroupVisibilityMode;
  updatedAt: number;
}

const STORAGE_KEYS = {
  TITLES: 'ssd_admin_custom_sheet_titles',
  PERMISSIONS: 'ssd_admin_custom_resource_permissions',
  TABS: 'ssd_admin_custom_tab_selection',
  USER_MAPPINGS: 'ssd_admin_user_mappings',
  TEAM_MEMBERS: 'ssd_admin_team_members',
  CUSTOM_TEAMS: 'ssd_admin_custom_teams',
  ARCHIVED_TEAMS: 'ssd_admin_archived_teams',
  DELETED_TEAMS: 'ssd_admin_deleted_teams',
  DISCOVERED_TABS: 'ssd_admin_discovered_tabs',
  EMPLOYEE_DIRECTORY: 'ssd_admin_employee_directory',
  ACTIVE_EMPLOYEES: 'ssd_admin_active_employees',
  TERMINATED_EMAILS: 'ssd_admin_terminated_emails',
  SHEET_CUSTOM_TEAMS: 'ssd_admin_sheet_custom_teams',
  TEAM_SHEET_ORDER: 'ssd_admin_team_sheet_order',
  PRINTABLE_DOC_GROUPS: 'ssd_admin_printable_doc_groups',
  TIMESTAMP: 'ssd_admin_settings_updated_at',
};

// BroadcastChannel for instant cross-tab real-time sync
const syncChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('ssd_global_admin_sync') : null;

// In-memory cache for ultra-fast nanosecond lookups and zero CPU lag on renders
let cachedAdminSettings: GlobalAdminSettings | null = null;

/**
 * Invalidate in-memory cache to force read from localStorage
 */
export function invalidateAdminSettingsCache(): void {
  cachedAdminSettings = null;
}

/**
 * Get current composite admin settings from local persistence (uses memory cache)
 */
export function getLocalAdminSettings(): GlobalAdminSettings {
  if (cachedAdminSettings) {
    return cachedAdminSettings;
  }

  let customSheetTitles = {};
  let resourcePermissions = {};
  let customTabSelections = {};
  let userMappings = {};
  let teamMembers = {};
  let customTeamsList: string[] = [];
  let archivedTeamsList: string[] = [];
  let deletedTeamsList: string[] = [];
  let discoveredTabs = [];
  let employeeDirectory = {};
  let activeEmployees: ActiveEmployeeRecord[] = [];
  let terminatedEmails: string[] = [];
  let sheetCustomTeams: string[] = [];
  let teamSheetOrder: Record<string, string[]> = {};
  let printableDocGroups: PrintableDocGroup[] = [];
  let updatedAt = 0;

  try {
    const deletedTeamsRaw = localStorage.getItem(STORAGE_KEYS.DELETED_TEAMS);
    if (deletedTeamsRaw) {
      deletedTeamsList = JSON.parse(deletedTeamsRaw).filter((t: string) => t && typeof t === 'string' && t.trim() !== '');
    }
    const deletedSet = new Set(deletedTeamsList.map(t => t.toLowerCase().trim()));

    const titlesRaw = localStorage.getItem(STORAGE_KEYS.TITLES);
    if (titlesRaw) customSheetTitles = JSON.parse(titlesRaw);

    const permsRaw = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    if (permsRaw) resourcePermissions = JSON.parse(permsRaw);

    const tabsRaw = localStorage.getItem(STORAGE_KEYS.TABS);
    if (tabsRaw) customTabSelections = JSON.parse(tabsRaw);

    const usersRaw = localStorage.getItem(STORAGE_KEYS.USER_MAPPINGS);
    if (usersRaw) {
      const parsedUsers = JSON.parse(usersRaw);
      Object.keys(parsedUsers).forEach(email => {
        if (parsedUsers[email].assignedTeams) {
          parsedUsers[email].assignedTeams = parsedUsers[email].assignedTeams.filter((t: string) => 
            t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())
          );
          if (parsedUsers[email].primaryTeam && (
            parsedUsers[email].primaryTeam.toLowerCase() === 'management' || 
            parsedUsers[email].primaryTeam.toLowerCase() === 'no team' ||
            deletedSet.has(parsedUsers[email].primaryTeam.toLowerCase().trim())
          )) {
            parsedUsers[email].primaryTeam = parsedUsers[email].assignedTeams.length > 0 ? parsedUsers[email].assignedTeams[0] : '';
          }
        }
      });
      userMappings = { ...DEFAULT_ROLE_MAPPINGS, ...parsedUsers };
    } else {
      userMappings = { ...DEFAULT_ROLE_MAPPINGS };
    }

    const membersRaw = localStorage.getItem(STORAGE_KEYS.TEAM_MEMBERS);
    if (membersRaw) teamMembers = JSON.parse(membersRaw);

    const customTeamsRaw = localStorage.getItem(STORAGE_KEYS.CUSTOM_TEAMS);
    if (customTeamsRaw) {
      customTeamsList = JSON.parse(customTeamsRaw).filter((t: string) => 
        t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())
      );
    }

    const archivedTeamsRaw = localStorage.getItem(STORAGE_KEYS.ARCHIVED_TEAMS);
    if (archivedTeamsRaw) {
      archivedTeamsList = JSON.parse(archivedTeamsRaw).filter((t: string) => 
        t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())
      );
    }

    const discoveredRaw = localStorage.getItem(STORAGE_KEYS.DISCOVERED_TABS);
    if (discoveredRaw) discoveredTabs = JSON.parse(discoveredRaw);

    const empRaw = localStorage.getItem(STORAGE_KEYS.EMPLOYEE_DIRECTORY);
    if (empRaw) employeeDirectory = JSON.parse(empRaw);

    const activeEmpRaw = localStorage.getItem(STORAGE_KEYS.ACTIVE_EMPLOYEES);
    if (activeEmpRaw) activeEmployees = JSON.parse(activeEmpRaw);

    const termRaw = localStorage.getItem(STORAGE_KEYS.TERMINATED_EMAILS);
    if (termRaw) terminatedEmails = JSON.parse(termRaw);

    const sheetCustomTeamsRaw = localStorage.getItem(STORAGE_KEYS.SHEET_CUSTOM_TEAMS);
    if (sheetCustomTeamsRaw) {
      sheetCustomTeams = JSON.parse(sheetCustomTeamsRaw).filter((t: string) => 
        t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())
      );
    }

    const orderRaw = localStorage.getItem(STORAGE_KEYS.TEAM_SHEET_ORDER);
    if (orderRaw) teamSheetOrder = JSON.parse(orderRaw);

    const groupsRaw = localStorage.getItem(STORAGE_KEYS.PRINTABLE_DOC_GROUPS);
    if (groupsRaw) {
      try {
        printableDocGroups = JSON.parse(groupsRaw);
      } catch {}
    }

    const tsRaw = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);
    if (tsRaw) updatedAt = parseInt(tsRaw, 10) || 0;
  } catch {
    // Ignore read errors
  }

  const result: GlobalAdminSettings = {
    customSheetTitles,
    resourcePermissions,
    customTabSelections,
    userMappings,
    teamMembers,
    customTeams: customTeamsList,
    archivedTeams: archivedTeamsList,
    deletedTeams: deletedTeamsList,
    discoveredTabs,
    employeeDirectory,
    activeEmployees,
    terminatedEmails,
    sheetCustomTeams,
    teamSheetOrder,
    printableDocGroups: Array.isArray(printableDocGroups) ? printableDocGroups : [],
    updatedAt,
  };

  cachedAdminSettings = result;
  return result;
}

/**
 * Save updated admin settings locally and push to global server storage
 */
export async function saveGlobalAdminSettings(partial: Partial<GlobalAdminSettings>): Promise<void> {
  const current = getLocalAdminSettings();

  const nextDeletedTeams = Array.from(new Set(
    (partial.deletedTeams !== undefined ? partial.deletedTeams : current.deletedTeams || [])
      .filter((t: string) => t && typeof t === 'string' && t.trim() !== '')
  ));
  const deletedSet = new Set(nextDeletedTeams.map(t => t.toLowerCase().trim()));

  const updated: GlobalAdminSettings = {
    customSheetTitles: partial.customSheetTitles !== undefined ? partial.customSheetTitles : current.customSheetTitles,
    resourcePermissions: partial.resourcePermissions !== undefined ? partial.resourcePermissions : current.resourcePermissions,
    customTabSelections: partial.customTabSelections !== undefined ? partial.customTabSelections : current.customTabSelections,
    userMappings: partial.userMappings !== undefined ? partial.userMappings : current.userMappings,
    teamMembers: partial.teamMembers !== undefined ? partial.teamMembers : current.teamMembers,
    customTeams: (partial.customTeams !== undefined ? partial.customTeams : current.customTeams)
      .filter((t: string) => t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())),
    archivedTeams: (partial.archivedTeams !== undefined ? partial.archivedTeams : current.archivedTeams)
      .filter((t: string) => t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())),
    deletedTeams: nextDeletedTeams,
    discoveredTabs: partial.discoveredTabs !== undefined ? partial.discoveredTabs : current.discoveredTabs,
    employeeDirectory: partial.employeeDirectory !== undefined ? partial.employeeDirectory : current.employeeDirectory,
    activeEmployees: partial.activeEmployees !== undefined ? partial.activeEmployees : current.activeEmployees,
    terminatedEmails: partial.terminatedEmails !== undefined ? partial.terminatedEmails : current.terminatedEmails,
    sheetCustomTeams: (partial.sheetCustomTeams !== undefined ? partial.sheetCustomTeams : current.sheetCustomTeams)
      .filter((t: string) => t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())),
    teamSheetOrder: partial.teamSheetOrder !== undefined ? partial.teamSheetOrder : (current.teamSheetOrder || {}),
    printableDocGroups: partial.printableDocGroups !== undefined ? partial.printableDocGroups : (current.printableDocGroups || []),
    updatedAt: Date.now(),
  };

  cachedAdminSettings = updated;

  // 1. Write to localStorage
  try {
    localStorage.setItem(STORAGE_KEYS.TITLES, JSON.stringify(updated.customSheetTitles));
    localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(updated.resourcePermissions));
    localStorage.setItem(STORAGE_KEYS.TABS, JSON.stringify(updated.customTabSelections));
    localStorage.setItem(STORAGE_KEYS.USER_MAPPINGS, JSON.stringify(updated.userMappings));
    localStorage.setItem(STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(updated.teamMembers));
    localStorage.setItem(STORAGE_KEYS.CUSTOM_TEAMS, JSON.stringify(updated.customTeams));
    localStorage.setItem(STORAGE_KEYS.ARCHIVED_TEAMS, JSON.stringify(updated.archivedTeams));
    localStorage.setItem(STORAGE_KEYS.DELETED_TEAMS, JSON.stringify(updated.deletedTeams));
    localStorage.setItem(STORAGE_KEYS.DISCOVERED_TABS, JSON.stringify(updated.discoveredTabs));
    localStorage.setItem(STORAGE_KEYS.EMPLOYEE_DIRECTORY, JSON.stringify(updated.employeeDirectory));
    localStorage.setItem(STORAGE_KEYS.ACTIVE_EMPLOYEES, JSON.stringify(updated.activeEmployees));
    localStorage.setItem(STORAGE_KEYS.TERMINATED_EMAILS, JSON.stringify(updated.terminatedEmails));
    localStorage.setItem(STORAGE_KEYS.SHEET_CUSTOM_TEAMS, JSON.stringify(updated.sheetCustomTeams));
    localStorage.setItem(STORAGE_KEYS.TEAM_SHEET_ORDER, JSON.stringify(updated.teamSheetOrder || {}));
    localStorage.setItem(STORAGE_KEYS.PRINTABLE_DOC_GROUPS, JSON.stringify(updated.printableDocGroups || []));
    localStorage.setItem(STORAGE_KEYS.TIMESTAMP, updated.updatedAt.toString());
  } catch {
    // Ignore storage write errors
  }

  // 2. Broadcast across open browser tabs
  if (syncChannel) {
    try {
      syncChannel.postMessage({ type: 'ADMIN_SETTINGS_UPDATED', payload: updated });
    } catch {}
  }

  // 3. Save to backend API preferences
  try {
    await saveGlobalAdminSettingsApi(updated);
  } catch {
    // Fallback gracefully if backend save fails
  }
}

/**
 * Completely and permanently delete a team globally across all configurations,
 * user mappings, sheet custom teams, permissions, and backend endpoints.
 */
export async function deleteGlobalTeam(teamName: string): Promise<void> {
  if (!teamName || typeof teamName !== 'string') return;
  const targetTrimmed = teamName.trim();
  if (!targetTrimmed) return;
  const targetLower = targetTrimmed.toLowerCase();

  const current = getLocalAdminSettings();
  const currentDeleted = current.deletedTeams || [];
  const nextDeleted = Array.from(new Set([...currentDeleted, targetTrimmed]));
  const deletedSet = new Set(nextDeleted.map(t => t.toLowerCase().trim()));

  // Filter customTeams, sheetCustomTeams, archivedTeams
  const nextCustom = (current.customTeams || []).filter(t => !deletedSet.has(t.toLowerCase().trim()));
  const nextSheetCustom = (current.sheetCustomTeams || []).filter(t => !deletedSet.has(t.toLowerCase().trim()));
  const nextArchived = (current.archivedTeams || []).filter(t => !deletedSet.has(t.toLowerCase().trim()));

  // Clean user mappings
  const nextUserMappings = { ...(current.userMappings || {}) };
  Object.keys(nextUserMappings).forEach(email => {
    const u = nextUserMappings[email];
    if (u && Array.isArray(u.assignedTeams)) {
      const cleanAssigned = u.assignedTeams.filter(t => !deletedSet.has(t.toLowerCase().trim()));
      nextUserMappings[email] = {
        ...u,
        assignedTeams: cleanAssigned,
        primaryTeam: deletedSet.has((u.primaryTeam || '').toLowerCase().trim()) ? (cleanAssigned[0] || '') : u.primaryTeam,
      };
    }
  });

  // Clean team members map
  const nextTeamMembers = { ...(current.teamMembers || {}) };
  delete nextTeamMembers[targetTrimmed];
  Object.keys(nextTeamMembers).forEach(k => {
    if (k.toLowerCase().trim() === targetLower) {
      delete nextTeamMembers[k];
    }
  });

  // Clean resource permissions
  const nextPermissions = { ...(current.resourcePermissions || {}) };
  Object.keys(nextPermissions).forEach(k => {
    const p = nextPermissions[k];
    if (p && p.team) {
      const remainingTeams = p.team.split(',').map(t => t.trim()).filter(t => !deletedSet.has(t.toLowerCase()));
      if (remainingTeams.length > 0) {
        nextPermissions[k] = { ...p, team: remainingTeams.join(', ') };
      } else {
        const { team: _, ...rest } = p;
        nextPermissions[k] = rest;
      }
    }
  });

  // Save locally and sync to KV
  await saveGlobalAdminSettings({
    deletedTeams: nextDeleted,
    customTeams: nextCustom,
    sheetCustomTeams: nextSheetCustom,
    archivedTeams: nextArchived,
    userMappings: nextUserMappings,
    teamMembers: nextTeamMembers,
    resourcePermissions: nextPermissions,
  });

  // Trigger backend remove-team API
  try {
    const token = getAuthToken();
    if (token) {
      fetch('/api/remove-team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ teamName: targetTrimmed }),
      }).catch(e => console.warn('[AdminSync] Failed to remove team from sheet:', e));
    }
  } catch (e) {
    console.warn('[AdminSync] Could not call remove-team endpoint:', e);
  }
}

/**
 * Fetch latest global admin settings from backend server and update local state if newer
 */
export async function syncAdminSettingsWithServer(): Promise<boolean> {
  try {
    const res = await getGlobalAdminSettingsApi();
    if (res.success && res.data) {
      const serverSettings = res.data as GlobalAdminSettings;
      const local = getLocalAdminSettings();

      // Combined deleted teams set
      const mergedDeletedTeams = Array.from(new Set([
        ...(local.deletedTeams || []),
        ...(serverSettings.deletedTeams || [])
      ])).filter((t: string) => t && typeof t === 'string' && t.trim() !== '');
      const deletedSet = new Set(mergedDeletedTeams.map(t => t.toLowerCase().trim()));

      // Always sync read-only server data (populated by background worker)
      let hasBackgroundChanges = false;
      if (serverSettings.employeeDirectory && Object.keys(serverSettings.employeeDirectory).length > 0) {
        localStorage.setItem(STORAGE_KEYS.EMPLOYEE_DIRECTORY, JSON.stringify(serverSettings.employeeDirectory));
        local.employeeDirectory = serverSettings.employeeDirectory;
        hasBackgroundChanges = true;
      }
      if (serverSettings.activeEmployees && serverSettings.activeEmployees.length > 0) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_EMPLOYEES, JSON.stringify(serverSettings.activeEmployees));
        local.activeEmployees = serverSettings.activeEmployees;
        hasBackgroundChanges = true;
      }
      if (serverSettings.terminatedEmails && serverSettings.terminatedEmails.length > 0) {
        localStorage.setItem(STORAGE_KEYS.TERMINATED_EMAILS, JSON.stringify(serverSettings.terminatedEmails));
        local.terminatedEmails = serverSettings.terminatedEmails;
        hasBackgroundChanges = true;
      }
      if (serverSettings.discoveredTabs && serverSettings.discoveredTabs.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DISCOVERED_TABS, JSON.stringify(serverSettings.discoveredTabs));
        local.discoveredTabs = serverSettings.discoveredTabs;
        hasBackgroundChanges = true;
      }
      if (serverSettings.sheetCustomTeams && serverSettings.sheetCustomTeams.length > 0) {
        const cleanSheetTeams = serverSettings.sheetCustomTeams.filter((t: string) => 
          t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())
        );
        localStorage.setItem(STORAGE_KEYS.SHEET_CUSTOM_TEAMS, JSON.stringify(cleanSheetTeams));
        local.sheetCustomTeams = cleanSheetTeams;
        hasBackgroundChanges = true;
      }

      if (!local.updatedAt || serverSettings.updatedAt > local.updatedAt) {
        console.log('[AdminSync] Server settings are newer. Updating local storage...');
        
        // Prevent legacy local-only data loss by merging
        const mergedCustomTeams = Array.from(new Set([...(local.customTeams || []), ...(serverSettings.customTeams || [])]))
          .filter((t: string) => t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim()));
        const mergedArchivedTeams = Array.from(new Set([...(local.archivedTeams || []), ...(serverSettings.archivedTeams || [])]))
          .filter((t: string) => t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim()));
        const cleanSheetCustomTeams = (serverSettings.sheetCustomTeams || [])
          .filter((t: string) => t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim()));

        const mergedTeamSheetOrder: Record<string, string[]> = {
          ...(local.teamSheetOrder || {}),
          ...(serverSettings.teamSheetOrder || {}),
        };
        // If local had a team order and server is missing it or server's list is empty, preserve local!
        if (local.teamSheetOrder) {
          Object.keys(local.teamSheetOrder).forEach((team) => {
            if (
              local.teamSheetOrder![team] &&
              local.teamSheetOrder![team].length > 0 &&
              (!mergedTeamSheetOrder[team] || mergedTeamSheetOrder[team].length === 0)
            ) {
              mergedTeamSheetOrder[team] = local.teamSheetOrder![team];
            }
          });
        }

        const mergedTitles = { ...(local.customSheetTitles || {}), ...(serverSettings.customSheetTitles || {}) };
        const mergedPermissions = { ...(local.resourcePermissions || {}), ...(serverSettings.resourcePermissions || {}) };
        const mergedTabs = { ...(local.customTabSelections || {}), ...(serverSettings.customTabSelections || {}) };
        const mergedUserMappings = { ...(local.userMappings || {}), ...(serverSettings.userMappings || {}) };
        const mergedTeamMembers = { ...(local.teamMembers || {}), ...(serverSettings.teamMembers || {}) };

        localStorage.setItem(STORAGE_KEYS.TITLES, JSON.stringify(mergedTitles));
        localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(mergedPermissions));
        localStorage.setItem(STORAGE_KEYS.TABS, JSON.stringify(mergedTabs));
        localStorage.setItem(STORAGE_KEYS.USER_MAPPINGS, JSON.stringify(mergedUserMappings));
        localStorage.setItem(STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(mergedTeamMembers));
        localStorage.setItem(STORAGE_KEYS.CUSTOM_TEAMS, JSON.stringify(mergedCustomTeams));
        localStorage.setItem(STORAGE_KEYS.ARCHIVED_TEAMS, JSON.stringify(mergedArchivedTeams));
        localStorage.setItem(STORAGE_KEYS.DELETED_TEAMS, JSON.stringify(mergedDeletedTeams));
        localStorage.setItem(STORAGE_KEYS.SHEET_CUSTOM_TEAMS, JSON.stringify(cleanSheetCustomTeams));
        localStorage.setItem(STORAGE_KEYS.TEAM_SHEET_ORDER, JSON.stringify(mergedTeamSheetOrder));
        if (serverSettings.printableDocGroups || local.printableDocGroups) {
          localStorage.setItem(STORAGE_KEYS.PRINTABLE_DOC_GROUPS, JSON.stringify(serverSettings.printableDocGroups || local.printableDocGroups || []));
        }
        localStorage.setItem(STORAGE_KEYS.TIMESTAMP, (serverSettings.updatedAt || Date.now()).toString());

        const mergedSettings: GlobalAdminSettings = {
          ...serverSettings,
          customSheetTitles: mergedTitles,
          resourcePermissions: mergedPermissions,
          customTabSelections: mergedTabs,
          userMappings: mergedUserMappings,
          teamMembers: mergedTeamMembers,
          customTeams: mergedCustomTeams,
          archivedTeams: mergedArchivedTeams,
          deletedTeams: mergedDeletedTeams,
          employeeDirectory: local.employeeDirectory,
          activeEmployees: local.activeEmployees,
          terminatedEmails: local.terminatedEmails,
          discoveredTabs: local.discoveredTabs,
          sheetCustomTeams: cleanSheetCustomTeams,
          teamSheetOrder: mergedTeamSheetOrder,
          printableDocGroups: serverSettings.printableDocGroups || local.printableDocGroups || [],
          updatedAt: Math.max(serverSettings.updatedAt || 0, local.updatedAt || 0, Date.now()),
        };

        cachedAdminSettings = mergedSettings;

        if (syncChannel) {
          try {
            syncChannel.postMessage({ type: 'ADMIN_SETTINGS_UPDATED', payload: mergedSettings });
          } catch {}
        }
        
        return true;
      } else if (hasBackgroundChanges) {
        cachedAdminSettings = { ...local, deletedTeams: mergedDeletedTeams };
        if (syncChannel) {
           try {
              syncChannel.postMessage({ type: 'ADMIN_SETTINGS_UPDATED', payload: cachedAdminSettings });
           } catch {}
        }
        return true;
      }
    }
  } catch (err) {
    console.warn('[AdminSync] Could not sync settings with server:', err);
  }
  return false;
}

/**
 * Save custom sheet order for a specific team
 */
export async function saveTeamSheetOrder(teamName: string, orderedSheetIds: string[]): Promise<void> {
  if (!teamName) return;
  const current = getLocalAdminSettings();
  const teamKey = teamName.trim();
  const currentOrder = current.teamSheetOrder || {};
  const updatedOrder = {
    ...currentOrder,
    [teamKey]: orderedSheetIds,
  };
  await saveGlobalAdminSettings({
    teamSheetOrder: updatedOrder,
  });
}

/**
 * Reset sheet order for a team back to default (A-Z)
 */
export async function resetTeamSheetOrder(teamName: string): Promise<void> {
  if (!teamName) return;
  const current = getLocalAdminSettings();
  const teamKey = teamName.trim();
  const currentOrder = { ...(current.teamSheetOrder || {}) };
  delete currentOrder[teamKey];
  Object.keys(currentOrder).forEach((k) => {
    if (k.toLowerCase().trim() === teamKey.toLowerCase().trim()) {
      delete currentOrder[k];
    }
  });
  await saveGlobalAdminSettings({
    teamSheetOrder: currentOrder,
  });
}

/**
 * Get custom sheet order for a team
 */
export function getTeamSheetOrder(teamName: string): string[] | null {
  if (!teamName) return null;
  const current = getLocalAdminSettings();
  const orderMap = current.teamSheetOrder || {};
  if (orderMap[teamName] && Array.isArray(orderMap[teamName])) {
    return orderMap[teamName];
  }
  const matchKey = Object.keys(orderMap).find((k) => k.toLowerCase().trim() === teamName.toLowerCase().trim());
  if (matchKey && Array.isArray(orderMap[matchKey])) {
    return orderMap[matchKey];
  }
  return null;
}

/**
 * Save or update a single printable document group
 */
export async function savePrintableDocGroup(group: PrintableDocGroup): Promise<void> {
  if (!group || !group.id) return;
  const current = getLocalAdminSettings();
  const groups = current.printableDocGroups || [];
  const existingIdx = groups.findIndex(g => g.id === group.id);
  let updatedGroups: PrintableDocGroup[];
  if (existingIdx >= 0) {
    updatedGroups = [...groups];
    updatedGroups[existingIdx] = {
      ...groups[existingIdx],
      ...group,
      updatedAt: Date.now(),
    };
  } else {
    updatedGroups = [
      ...groups,
      {
        ...group,
        createdAt: group.createdAt || Date.now(),
        updatedAt: Date.now(),
      },
    ];
  }
  await saveGlobalAdminSettings({
    printableDocGroups: updatedGroups,
  });
}

/**
 * Save all printable document groups at once
 */
export async function savePrintableDocGroups(groups: PrintableDocGroup[]): Promise<void> {
  await saveGlobalAdminSettings({
    printableDocGroups: groups,
  });
}

/**
 * Permanently delete a printable document group
 */
export async function deletePrintableDocGroup(groupId: string): Promise<void> {
  if (!groupId) return;
  const current = getLocalAdminSettings();
  const groups = current.printableDocGroups || [];
  const updatedGroups = groups.filter(g => g.id !== groupId);
  await saveGlobalAdminSettings({
    printableDocGroups: updatedGroups,
  });
}

/**
 * Archive or unarchive a printable document group
 */
export async function archivePrintableDocGroup(groupId: string, archived: boolean): Promise<void> {
  if (!groupId) return;
  const current = getLocalAdminSettings();
  const groups = current.printableDocGroups || [];
  const updatedGroups = groups.map(g => {
    if (g.id === groupId) {
      return { ...g, archived, updatedAt: Date.now() };
    }
    return g;
  });
  await saveGlobalAdminSettings({
    printableDocGroups: updatedGroups,
  });
}

/**
 * Get all printable document groups
 */
export function getPrintableDocGroups(): PrintableDocGroup[] {
  const current = getLocalAdminSettings();
  return current.printableDocGroups || [];
}

/**
 * Filter groups based on visibility settings and user identity
 * Default mode is 'everyone' (all users can see groups), but extensible if visibility needs to be restricted.
 */
export function isGroupVisibleToUser(
  group: PrintableDocGroup,
  userEmail?: string | null,
  mode: GroupVisibilityMode = 'everyone'
): boolean {
  if (!group || group.archived) return false;
  const email = (userEmail || '').toLowerCase().trim();

  // Mode 1: 'everyone' (default requested)
  // All public groups are visible to everybody.
  // If an individual group is marked 'private', it is only shown to its creator.
  if (mode === 'everyone') {
    if (group.visibility === 'private' && group.createdBy) {
      return email.length > 0 && group.createdBy.toLowerCase() === email;
    }
    return true;
  }

  // Mode 2: 'own_only'
  // Users only see groups created by themselves (plus system/legacy groups without createdBy).
  if (mode === 'own_only') {
    if (!group.createdBy) return true;
    return email.length > 0 && group.createdBy.toLowerCase() === email;
  }

  // Mode 3: 'public_and_own'
  // Users see public groups, their own groups, or groups where their email is in allowedEmails.
  if (mode === 'public_and_own') {
    if (!group.visibility || group.visibility === 'public') return true;
    if (group.createdBy && email.length > 0 && group.createdBy.toLowerCase() === email) return true;
    if (group.allowedEmails && email.length > 0 && group.allowedEmails.some(e => e.toLowerCase() === email)) return true;
    return false;
  }

  return true;
}

/**
 * Subscribe to real-time admin sync changes
 */
export function onAdminSettingsChange(callback: (settings: GlobalAdminSettings) => void): () => void {
  if (!syncChannel) return () => {};

  const handler = (event: MessageEvent) => {
    if (event.data && event.type === 'message' && event.data.type === 'ADMIN_SETTINGS_UPDATED' && event.data.payload) {
      cachedAdminSettings = event.data.payload;
      callback(event.data.payload);
    }
  };

  syncChannel.addEventListener('message', handler);
  return () => {
    syncChannel.removeEventListener('message', handler);
  };
}
